// Stacked area chart for time-bucketed timelines. Used by the landing-page
// hero (country-stacked monthly blog volume).
import type { CSSProperties } from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Label,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import ChartTooltip from './ChartTooltip';

export interface StackedAreaSeries {
  key: string;
  label: string;
  color: string;
}

interface Props {
  data: Array<Record<string, number | string>>;
  series: StackedAreaSeries[];
  height?: number;
  // Column key used for the X axis. Defaults to 'ym' for back-compat with
  // older callers; the landing hero payload uses 't'.
  xKey?: string;
  // When provided, drives the initial visible-band set. Keys not in this
  // set are hidden until the user toggles them via the legend.
  defaultVisibleKeys?: Set<string> | string[];
  // When false, the chart renders all series and hides the legend (back-
  // compat behaviour). Defaults to true.
  interactiveLegend?: boolean;
  // When set, that series' value per row absorbs the per-row value of
  // every currently-hidden series. Used by the landing hero so toggling
  // a country off pushes its volume into the "Other" band rather than
  // dropping it from the chart.
  rollupHiddenIntoKey?: string;
  // Vertical reference lines at specific X values (e.g. project launch
  // dates). A marker is only drawn when its ``x`` matches a row in the
  // current window — outside the window the line silently noops.
  markers?: Array<{ x: string; label?: string; color?: string }>;
}

export default function StackedArea({
  data,
  series,
  height = 280,
  xKey = 'ym',
  defaultVisibleKeys,
  interactiveLegend = true,
  rollupHiddenIntoKey,
  markers,
}: Props) {
  const allKeys = useMemo(() => series.map((s) => s.key), [series]);

  const initialVisible = useMemo<Set<string>>(() => {
    if (defaultVisibleKeys === undefined) return new Set(allKeys);
    const provided =
      defaultVisibleKeys instanceof Set
        ? defaultVisibleKeys
        : new Set(defaultVisibleKeys);
    // Intersect with the actual key catalog so stale keys from a previous
    // payload don't linger.
    return new Set(allKeys.filter((k) => provided.has(k)));
  }, [allKeys, defaultVisibleKeys]);

  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(initialVisible);

  // When the series catalog changes (range switch), re-seed visibility so
  // newly-added keys honour their default_visible flag and removed keys
  // don't sit in the set forever.
  useEffect(() => {
    setVisibleKeys(initialVisible);
  }, [initialVisible]);

  if (!data || data.length === 0 || series.length === 0) {
    return <div className="trends-chart-empty">No data in this range.</div>;
  }

  const toggle = (key: string) => {
    setVisibleKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const visibleSeries = series.filter((s) => visibleKeys.has(s.key));

  // Optional X-axis annotations (e.g. project launch dates). Only rendered
  // when the marker's x value is present in the row dataset — otherwise
  // Recharts would draw the line at an arbitrary edge.
  const xValueSet = useMemo(() => {
    const s = new Set<string | number>();
    for (const row of data) s.add(row[xKey] as string | number);
    return s;
  }, [data, xKey]);

  // When rollupHiddenIntoKey is set and that key is itself visible, fold
  // each hidden series' per-row value into the target so the "residual"
  // band always represents "everything not currently selected" rather
  // than the static backend bucket.
  const chartData = useMemo(() => {
    if (!rollupHiddenIntoKey) return data;
    const targetSeries = series.find((s) => s.key === rollupHiddenIntoKey);
    if (!targetSeries) return data;
    const hiddenKeys = series
      .map((s) => s.key)
      .filter((k) => k !== rollupHiddenIntoKey && !visibleKeys.has(k));
    if (hiddenKeys.length === 0) return data;
    return data.map((row) => {
      let extra = 0;
      for (const k of hiddenKeys) {
        const v = row[k];
        if (typeof v === 'number') extra += v;
      }
      if (extra === 0) return row;
      const base = row[rollupHiddenIntoKey];
      const baseNum = typeof base === 'number' ? base : 0;
      return { ...row, [rollupHiddenIntoKey]: baseNum + extra };
    });
  }, [data, series, visibleKeys, rollupHiddenIntoKey]);

  return (
    <>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
          <XAxis dataKey={xKey} fontSize={11} tickMargin={4} />
          <YAxis fontSize={11} tickMargin={4} />
          <Tooltip content={<ChartTooltip />} />
          {markers
            ?.filter((m) => xValueSet.has(m.x))
            .map((m) => (
              <ReferenceLine
                key={m.x}
                x={m.x}
                stroke={m.color ?? '#d04848'}
                strokeWidth={2}
                strokeDasharray="4 3"
                ifOverflow="extendDomain"
              >
                {m.label && (
                  <Label
                    value={m.label}
                    position="insideTopRight"
                    fill={m.color ?? '#d04848'}
                    fontSize={11}
                    offset={8}
                  />
                )}
              </ReferenceLine>
            ))}
          {visibleSeries.map((s) => (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stackId="1"
              stroke={s.color}
              fill={s.color}
              fillOpacity={0.7}
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
      {interactiveLegend && (
        <div className="chart-legend-strip" role="group" aria-label="Series visibility">
          {series.map((s) => {
            const active = visibleKeys.has(s.key);
            return (
              <button
                key={s.key}
                type="button"
                className={`chart-legend-chip${active ? '' : ' inactive'}`}
                onClick={() => toggle(s.key)}
                aria-pressed={active}
                style={{ '--chip-swatch-color': s.color } as CSSProperties}
                title={active ? `Hide ${s.label}` : `Show ${s.label}`}
              >
                <span
                  className="chart-legend-swatch"
                  style={{ background: s.color }}
                  aria-hidden
                />
                <span className="chart-legend-name">{s.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}
